import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import "./styles.css";
import RoutesConfig from "./routes";
import { useQueryClient } from "@tanstack/react-query";
import localforage from "localforage";

import { ToastContainer } from "react-toastify";
import CartOffCanvas from "./pages/Website/Cart";

import Wishlist from "./pages/Website/Wishlist";

import Footer from "./components/Footer/Footer";
import GuestNavbar, {
  getParsedLocalStorageItem,
} from "./components/Navbar/Navbar";
import MobileNavbar from "./components/Navbar/MobileNavbar";

import LogoutModal from "./components/Modal/Logout";

import useSavePreviousPath from "./hooks/useSavePreviousPath";
import { useAppContext } from "./AppContext";
import { trackPageView } from "./services/tracking";
import VirtuePlusModal from "./components/Modal/VirtuePlusModal";
import { trackEvent } from "./mixpanel";
import SourceModal from "./components/Modal/SourceModal";
import ChatWidget from "./components/Chat/ChatWidget";
import CookieConsent from "./components/Modal/CookieConsent";
import { trackOmnisendPageViewed } from "./omnisend";
import {
  fetchCategories,
  fetchGlobalProducts,
  fetchTrendingProducts,
  fetchProducts,
} from "./services/productService";
import { fetchDeliveryStates } from "./services/locationService";

const ENV_PREFIX = process.env.REACT_APP_ENV_PREFIX || "production";

export const localStorageSetItem = (key, value) => {
  localStorage.setItem(`${ENV_PREFIX}_${key}`, value);
};

export const localStorageGetItem = (key) => {
  return localStorage.getItem(`${ENV_PREFIX}_${key}`);
};

export const localStorageRemoveItem = (key) => {
  localStorage.removeItem(`${ENV_PREFIX}_${key}`);
};

export const sessionStorageSetItem = (key, value) => {
  sessionStorage.setItem(`${ENV_PREFIX}_${key}`, value);
};

export const sessionStorageGetItem = (key) => {
  return sessionStorage.getItem(`${ENV_PREFIX}_${key}`);
};

// const clearUnprefixedLocalStorageItems = () => {
//   const keysToRemove = [];

//   // Iterate through all keys in local storage
//   for (let i = 0; i < localStorage?.length; i++) {
//     const key = localStorage?.key(i);

//     // Check if the key does not start with the prefix
//     if (key && !key?.startsWith(ENV_PREFIX)) {
//       keysToRemove.push(key); // Collect keys to remove
//     }
//   }

//   // Remove each unprefixed key
//   keysToRemove.forEach(key => localStorage.removeItem(key));
// };

export const transformPath = (path) => {
  // Handle null, undefined, or non-string paths
  if (!path || typeof path !== "string") {
    return "Unknown Page";
  }

  // Check for static paths first
  switch (path) {
    case "/":
      return "Home Page";
    case "/terms":
      return "Terms of Use";
    case "/privacy-policy":
      return "Privacy policy";
    case "/shipping-policy":
      return "Shipping policy";
    case "/cart":
      return "Cart Page";
    case "/vouchers":
      return "Vouchers Page";
    case "/foodbundles":
      return "Food Bundles Page";
    case "/refund-policy":
      return "Refund policy";
    default:
      if (path.startsWith("/category/")) {
        return "Category Listing Page";
      }
      if (path.startsWith("/products/")) {
        return "Product Details Page";
      } else {
        return path;
      }
  }
};
export const ProtectedRoute = ({ children }) => {
  const userData = getParsedLocalStorageItem("userData");
  const token = localStorageGetItem("userToken");
  const isLoggedIn = !!userData && !!token;
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login"); // Navigate to the home page
      // setShowLoginModal(true); // Show the login modal
    }
  }, [isLoggedIn, navigate]);

  return isLoggedIn ? <Outlet /> : null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const { isMobile } = useAppContext();
  const hideNavbarFooterRoutes = [
    "/login",
    "/signup",
    "/forgot-password",
    "/voucher-receiver",
  ];
  const hideMobileBottomNavRoutes = ["/order-summary"];
  const hideDesktopFooterRoutes = ["/reviews"];

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      {!hideNavbarFooterRoutes.includes(location.pathname) && <GuestNavbar />}

      <div style={{ flex: "1 0 auto" }}>{children}</div>

      {!hideNavbarFooterRoutes.includes(location.pathname) &&
        (isMobile ? (
          <MobileNavbar />
        ) : (
          !hideDesktopFooterRoutes.includes(location.pathname) && <Footer />
        ))}
    </div>
  );
};

export const PathTracker = ({ children }) => {
  useSavePreviousPath();
  return children;
};

export const PreventLoginAccessRoute = () => {
  const userData = getParsedLocalStorageItem("userData");
  const token = localStorageGetItem("userToken");
  const isLoggedIn = !!userData && !!token;
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) {
      const preAuthPath = localStorageGetItem("preAuthPath") || "/";
      navigate(preAuthPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return !token ? <Outlet /> : null;
};

function App() {
  const { isMobile, showWishlist, showCart } = useAppContext();
  const [showVirtueModal, setShowVirtueModal] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [virtueModalClosed, setVirtueModalClosed] = useState(false);

  const userData = getParsedLocalStorageItem("userData");
  const token = localStorageGetItem("userToken");
  const location = useLocation();
  const queryClient = useQueryClient();

  // useEffect(() => {
  //   initOmnisend();
  // }, []);

  useEffect(() => {
    trackPageView(location, userData);
    if (userData) {
      trackOmnisendPageViewed(userData);
    }
  }, [location.pathname]);

  useEffect(() => {
    const hasSeenModal =
      sessionStorageGetItem("hasSeenVirtuePlusModal") === "true";
    const isAuthPage =
      location.pathname.includes("/login") ||
      location.pathname.includes("/signup");

    if (!hasSeenModal && !isAuthPage && (!token || !userData)) {
      const timer = setTimeout(() => {
        setShowVirtueModal(true);
        trackEvent("Show Virtue+ Modal", {
          login_type: "email",
          seen_at: new Date().toISOString(),
          device_type: navigator.userAgent,
        });
        sessionStorageSetItem("hasSeenVirtuePlusModal", "true");
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      setShowVirtueModal(false);
    }
  }, [token, userData, location.pathname]);

  // Show cookie consent only if virtue modal was shown & closed, OR previously seen
  useEffect(() => {
    const consent = localStorageGetItem("cookieConsent");
    const hasSeenVirtueModal =
      sessionStorageGetItem("hasSeenVirtuePlusModal") === "true";

    if (
      !consent &&
      (virtueModalClosed || (hasSeenVirtueModal && !showVirtueModal))
    ) {
      const timer = setTimeout(() => {
        setShowCookieConsent(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [virtueModalClosed, showVirtueModal]);

  // SMART PREFETCHING + QUIET BACKGROUND REFRESH ===
  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        // Prefetch categories (frequently used and rarely change)
        queryClient.prefetchQuery({
          queryKey: ["categories"],
          queryFn: fetchCategories,
        });

        // Prefetch delivery states
        queryClient.prefetchQuery({
          queryKey: ["deliveryStates"],
          queryFn: fetchDeliveryStates,
        });

        // Prefetch global products (no pagination)
        // queryClient.prefetchQuery({
        //   queryKey: ["products", "global"],
        //   queryFn: fetchGlobalProducts,
        // });
        // Device-specific prefetching
        if (isMobile) {
          // Prefetch mobile infinite query "All" category
          queryClient.prefetchInfiniteQuery({
            queryKey: [
              "products",
              "infinite",
              { size: 20, search: "", categoryId: undefined },
            ],
            queryFn: ({ pageParam = 1 }) =>
              fetchProducts({ page: pageParam, size: 20, search: "" }),
            initialPageParam: 1,
          });
        } else {
          // Prefetch first page of products (for desktop)
          queryClient.prefetchQuery({
            queryKey: ["products", { page: 1, size: 20, search: "" }],
            queryFn: () => fetchProducts({ page: 1, size: 20, search: "" }),
          });
        }
        // Prefetch trending products (aligned with hook key)
        queryClient.prefetchQuery({
          queryKey: ["products", "trending"],
          queryFn: fetchTrendingProducts,
        });
      } catch (error) {
        console.error("Prefetch error:", error);
      }
    };

    // Run once on app load
    fetchPublicData();

    // Quiet background refresh every 10 min (keeps cache fresh if tab is open)
    const interval = setInterval(
      () => {
        fetchPublicData();
      },
      10 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [queryClient]);

  // SMOOTH MIGRATION: LocalStorage -> IndexedDB for Query Cache
  useEffect(() => {
    const migrateQueryCache = async () => {
      try {
        const oldKey = "react-query";
        const oldData = localStorage.getItem(oldKey);

        if (oldData) {
          // Check if IndexedDB already has data to avoid overwriting newer data
          const existingIDBData = await localforage.getItem(oldKey);
          if (!existingIDBData) {
            const parsedData = JSON.parse(oldData);
            await localforage.setItem(oldKey, parsedData);
          }

          localStorage.removeItem(oldKey);
        }
      } catch (error) {
        console.error("Migration Error:", error);
      }
    };

    migrateQueryCache();
  }, []);

  const closeModal = () => {
    setShowVirtueModal(false);
    setVirtueModalClosed(true);
    sessionStorageSetItem("hasSeenVirtuePlusModal", "true");
  };

  const acceptCookies = () => {
    localStorageSetItem("cookieConsent", "true");
    setShowCookieConsent(false);
  };

  return (
    <>
      <ToastContainer
        className="mx-2 mx-md-0 mt-3 mt-md-0"
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: "11000" }}
      />

      <LogoutModal />
      <VirtuePlusModal showModal={showVirtueModal} closeModal={closeModal} />
      <CookieConsent show={showCookieConsent} onAccept={acceptCookies} />
      <SourceModal />

      {!isMobile && showCart && <CartOffCanvas />}
      {showWishlist && <Wishlist />}

      <ScrollToTop />

      <ChatWidget />

      <Layout>
        <PathTracker>
          <RoutesConfig isMobile={isMobile} />
        </PathTracker>
      </Layout>
    </>
  );
}

export default App;
